import { useState, useEffect, useCallback } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs';
import { BaselinePanel } from './BaselinePanel';
import { SignalOverridesPanel } from './SignalOverridesPanel';
import { RecommendationsPanel } from './RecommendationsPanel';
import { getTenantTuning, type TenantTuningData } from '../../services/tuning';

interface TenantTuningSectionProps {
  tenantId: string;
}

export function TenantTuningSection({ tenantId }: TenantTuningSectionProps) {
  const [data, setData] = useState<TenantTuningData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    const result = await getTenantTuning(tenantId);

    if (result.success) {
      setData(result.data);
    } else {
      setError(result.error ?? 'Failed to load tuning data');
    }

    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-5 h-5 animate-spin text-text-secondary" />
        <span className="ml-2 text-sm text-text-secondary">Loading tuning data...</span>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-red-500 mb-3">{error}</p>
        <Button variant="secondary" size="sm" onClick={fetchData}>
          <RefreshCw className="w-3.5 h-3.5" />
          Retry
        </Button>
      </div>
    );
  }

  if (!data) return null;

  const overrideCount = data.overrides.length;
  const recCount = data.recommendations.length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wide">
          Tuning Configuration
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchData}
          disabled={loading}
        >
          <RefreshCw className={loading ? 'w-3.5 h-3.5 animate-spin' : 'w-3.5 h-3.5'} />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="baseline">
        <TabsList>
          <TabsTrigger value="baseline">Baseline</TabsTrigger>
          <TabsTrigger value="overrides">
            Overrides{overrideCount > 0 ? ` (${overrideCount})` : ''}
          </TabsTrigger>
          <TabsTrigger value="recommendations">
            Recommendations{recCount > 0 ? ` (${recCount})` : ''}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="baseline">
          <BaselinePanel
            baseline={data.profile?.computedBaseline ?? null}
            computedAt={data.profile?.computedAt ?? null}
          />
        </TabsContent>

        <TabsContent value="overrides">
          <SignalOverridesPanel
            tenantId={tenantId}
            overrides={data.overrides}
            onRefresh={fetchData}
          />
        </TabsContent>

        <TabsContent value="recommendations">
          <RecommendationsPanel recommendations={data.recommendations} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
